/**
 * AlimTalkPresenter
 * --
 */
import React from 'react';
import { Content, Title, Row, Col, Text } from 'components';
import { Spin, Table, Tag, Checkbox, Button, Flex, Input } from 'antd';
import MessageAlert, { stringToMoneyFormat } from 'utils';

const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
  },

  filterWrap: {
    display: 'flex',
    flexDirection: 'column',
    // gap: 12,
  },
  filterItem: {
    display: 'flex',
    gap: 24,
    alignItems: 'stretch',
    borderBottom: '1px solid #DDDDDD',
  },
  filterLabel: {
    width: 150,
    background: '#F4F4F4',
    padding: '12px 10px',
    borderRight: '1px solid #DDDDDD',
  },
  filterValue: {
    flex: 1,
    padding: '24px 0px',
  },
};

const STATUS = {
  REG: {
    title: '등록',
    color: 'default',
  },
  REQ: {
    title: '검수요청',
    color: 'processing',
  },
  REJ: {
    title: '반려',
    color: 'blue',
  },
  STP: {
    title: '차단',
    color: 'error',
  },
  RDY: {
    title: '발송전',
    color: 'default',
  },
  ACT: {
    title: '정상',
    color: 'green',
  },
  DMT: {
    title: '휴면',
    color: 'default',
  },
  BLK: {
    title: '차단',
    color: 'red',
  },
};

const AlimTalkPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    scrollRef,
    isFetch,
    isLoading,
    data,
    detail,
    onSelect,
    users,
    grades,
    selectedUsers,
    onUserSelect,
    setIsLoading,
    onSubmit,
    onRegister,
    inputValue,
    setInputValue,
  } = props;
  /* ===== STATE ===== */
  // 회원 필터 목록
  const [userList, setUserList] = React.useState([...users]);
  // 선택된 필터
  const [filter, setFilter] = React.useState({});

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 회원 검색
   * @param {string} type
   */
  const handleSearchList = (type = 'search') => {
    setIsLoading(true);

    let arr = [];
    // 검색 초기화
    if (type === 'reset') {
      setFilter({});
      arr = users;
    }
    // 필터값에 따른 회원 필터
    else {
      const filterList = users.filter((user) =>
        Object.entries(filter).every(([key, validValues]) =>
          validValues.includes(user[key])
        )
      );

      arr = filterList;
    }

    setTimeout(() => {
      setIsLoading(false);
      setUserList(arr);
    }, 1000);
  };

  /**
   * 필터 체크 핸들러
   * --
   * @param {string} t
   * @param {array} v
   */
  const handleSelected = (t, v) => {
    setFilter((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /* ===== HOOKS ===== */
  const columns = React.useMemo(() => {
    return [
      {
        title: '이름',
        dataIndex: 'user_name',
        render: (text) => text ?? '비회원',
        width: 100,
      },
      {
        title: '연락처',
        dataIndex: 'phone',
        render: (text) => (text ? text : '-'),
        width: 200,
      },
      {
        title: '등급',
        dataIndex: 'grade_name',
        width: 100,
      },
      {
        title: '회원 여부',
        dataIndex: 'is_member',
      },
      {
        title: '푸시알림 동의 여부',
        dataIndex: 'push_notification',
      },
      {
        title: '프로모션/이벤트 알림 동의 여부',
        dataIndex: 'event_notification',
      },
      {
        title: '마케팅정보 수집 및 활용 동의 여부',
        dataIndex: 'marketing_consent',
      },
      {
        title: '혜택 및 광고성 정보 수신 동의 여부',
        dataIndex: 'terms_of_ad',
      },
      {
        title: '마케팅 목적의 개전정보 수집 및 이용 동의 여부',
        dataIndex: 'terms_of_marketing',
      },
    ];
  }, []);

  // NOTE: 필터 목록
  const FilterItems = React.useMemo(() => {
    const filterItems = [
      ...(grades && grades.length
        ? [
            {
              title: '등급',
              key: 'grade_id',
              children: grades,
            },
          ]
        : []),
      {
        title: '회원 여부',
        key: 'is_member',
        children: [
          {
            label: '회원',
            value: 'Y',
          },
          {
            label: '비회원',
            value: 'N',
          },
        ],
      },
      {
        title: '푸시알림 동의 여부',
        key: 'push_notification',
        children: [
          {
            label: '동의함',
            value: 'Y',
          },
          {
            label: '동의안함',
            value: 'N',
          },
        ],
      },
      {
        title: '프로모션/이벤트 알림 동의 여부',
        key: 'event_notification',
        children: [
          {
            label: '동의함',
            value: 'Y',
          },
          {
            label: '동의안함',
            value: 'N',
          },
        ],
      },
      {
        title: '마케팅정보 수집 및 활용 동의 여부',
        key: 'marketing_consent',
        children: [
          {
            label: '동의함',
            value: 'Y',
          },
          {
            label: '동의안함',
            value: 'N',
          },
        ],
      },
      {
        title: '혜택 및 광고성 정보 수신 동의 여부',
        key: 'terms_of_ad',
        children: [
          {
            label: '동의함',
            value: 'Y',
          },
          {
            label: '동의안함',
            value: 'N',
          },
        ],
      },
      {
        title: '마케팅 목적의 개전정보 수집 및 이용 동의 여부',
        key: 'terms_of_marketing',
        children: [
          {
            label: '동의함',
            value: 'Y',
          },
          {
            label: '동의안함',
            value: 'N',
          },
        ],
      },
    ];

    return filterItems;
  }, [grades]);

  React.useEffect(() => {
    setUserList([...users]);
  }, [users]);

  /* ===== RENDER ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Title style={{ ...styles.title }}>알림톡 관리</Title>

          <Row gutter={[5, 5]}>
            <Col x={4}>
              <Content maxWidth={'100%'} borderRadius={4} height={'100%'}>
                <Title size={5} style={{ marginBottom: 20 }}>
                  템플릿 목록
                </Title>

                <div
                  style={{
                    height: 'calc(100% - 50px)',
                    background: '#F4F4F4',
                    overflowY: 'auto',
                  }}
                >
                  {data?.data?.list?.map((_item) => (
                    <div
                      key={`${_item.templateCode}`}
                      style={{
                        padding: 10,
                        borderBottom: '1px solid #DDDDDD',

                        position: 'relative',
                        top: 0,
                        left: 0,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          background:
                            detail &&
                            detail?.templateCode === _item?.templateCode
                              ? '#1C6BFF4D'
                              : 'transparent',
                          border:
                            detail &&
                            detail?.templateCode === _item?.templateCode
                              ? '1px solid #2394E4'
                              : 'none',
                        }}
                        onClick={() => onSelect(_item.templateCode)}
                      ></div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Text weight={700}>{_item.templateName}</Text>
                        <Tag color={STATUS[_item.serviceStatus].color}>
                          {STATUS[_item.serviceStatus].title}
                        </Tag>
                      </div>
                      <Text color={'#666666'}>{_item.templateCode}</Text>
                    </div>
                  ))}

                  {!data || data?.data?.hasNext ? (
                    <div ref={scrollRef}>
                      <Spin
                        spinning={isFetch}
                        style={{ width: '100%', padding: '10px 0px' }}
                      />
                    </div>
                  ) : null}
                </div>
              </Content>
            </Col>

            {/* 알림톡 예시 */}
            {console.log('detaul ===>', detail)}
            <Col x={6}>
              {detail ? (
                <Content
                  maxWidth={'100%'}
                  borderRadius={4}
                  height={'100%'}
                  padding={0}
                >
                  <div
                    style={{
                      borderBottom: '1px solid #EEEEEE',
                      padding: '14px 10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text weight={700}>{detail?.templateName}</Text>

                    <Button
                      disabled={detail?.is_register}
                      onClick={() => {
                        if (detail?.templateMessageType !== 'BA') {
                          MessageAlert.info('기본형 템플릿만 등록 가능합니다!');
                          return;
                        }

                        onRegister();
                      }}
                    >
                      템플릿 저장
                    </Button>
                  </div>

                  <Content
                    maxWidth={'80%'}
                    backgroundColor="#dbe7fa"
                    style={{ marginTop: 20 }}
                  >
                    <div>
                      <div
                        style={{
                          background: 'var(--kakao-color)',
                          padding: 10,
                          borderTopLeftRadius: 10,
                          borderTopRightRadius: 10,
                        }}
                      >
                        <Text weight={700}>알림톡 도착</Text>
                      </div>
                      {/* 이미지 */}
                      {detail?.templateImageUrl ? (
                        <div style={{ background: '#FFFFFF' }}>
                          <img
                            src={detail?.templateImageUrl}
                            alt={detail?.templateImageName}
                            width={'100%'}
                            height={'100%'}
                          />
                        </div>
                      ) : null}

                      <div
                        style={{
                          background: '#FFFFFF',
                          padding: 10,
                          borderBottomLeftRadius: 10,
                          borderBottomRightRadius: 10,
                        }}
                      >
                        {/* 헤더 */}
                        {detail?.templateHeader ? (
                          <div
                            style={{
                              padding: '10px 0px',
                              borderBottom: '1px solid #DDDDDD',
                            }}
                          >
                            <Text size={14}>{detail?.templateHeader}</Text>
                          </div>
                        ) : null}

                        {/* 강조표시 */}
                        {detail?.templateSubtitle ? (
                          <div
                            style={{
                              padding: '5px 0px',
                            }}
                          >
                            <Text size={14}>{detail?.templateSubtitle}</Text>
                          </div>
                        ) : null}
                        {detail?.templateTitle ? (
                          <div
                            style={{
                              padding: '0px 0px 5px',
                            }}
                          >
                            <Text size={20} weight={700}>
                              {detail?.templateTitle}
                            </Text>
                          </div>
                        ) : null}

                        {/* 아이템 하이라이트 */}
                        {detail?.templateItemHighlight ? (
                          <div
                            style={{
                              padding: '10px 0px',
                              borderBottom: '1px solid #DDDDDD',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <Text>{detail?.templateItemHighlight.title}</Text>
                              {detail?.templateItemHighlight.description ? (
                                <Text size={14} color={'#BDBDBD'}>
                                  {detail?.templateItemHighlight.description}
                                </Text>
                              ) : null}
                            </div>
                            {detail?.templateItemHighlight.imageUrl ? (
                              <div>
                                <img
                                  src={detail?.templateItemHighlight.imageUrl}
                                  alt={detail?.templateItemHighlight.title}
                                  width={50}
                                  height={50}
                                />
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {/* 아이템리스트 */}
                        {detail?.templateItem ? (
                          <div
                            style={{
                              padding: '10px 0px',
                              borderBottom: '1px solid #DDDDDD',
                            }}
                          >
                            {Object.entries(detail?.templateItem).map(
                              ([key, value], index) => (
                                <div key={`${key}-${index}`}>
                                  {key === 'list' ? (
                                    value.map((item, i) => (
                                      <div
                                        key={`list-${i}`}
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Text size={14} color={'#BDBDBD'}>
                                          {item?.title}
                                        </Text>
                                        <Text size={14}>
                                          {item?.description}
                                        </Text>
                                      </div>
                                    ))
                                  ) : (
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Text size={14} color={'#BDBDBD'}>
                                        {value?.title}
                                      </Text>
                                      <Text size={18}>
                                        {value?.description}
                                      </Text>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        ) : null}

                        {/* 내용 */}
                        <div style={{ padding: '10px 0px' }}>
                          <pre>{detail?.templateContent}</pre>
                        </div>

                        {detail?.buttons?.length ? (
                          <Flex gap={8} vertical style={{ marginTop: 30 }}>
                            {detail?.buttons?.map((button, index) => (
                              <button
                                key={`button-${index}`}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  border: 'none',
                                }}
                              >
                                {button?.name}
                              </button>
                            ))}
                          </Flex>
                        ) : null}
                      </div>
                    </div>
                  </Content>

                  {/* 변수 입력 */}
                  <Content maxWidth={'100%'}>
                    <Row gutter={[12, 12]}>
                      {detail?.is_register && detail?.placeholder
                        ? Object.keys(detail?.placeholder)?.map(
                            (key, index) => (
                              <React.Fragment
                                key={`placeholder-${key}-${index}`}
                              >
                                <Col x={10}>
                                  <Input value={key} readOnly />
                                </Col>
                                <Col x={14}>
                                  <Input
                                    value={inputValue[key]}
                                    placeholder="변수에 전달할 값을 입력해주세요"
                                    onChange={(e) =>
                                      setInputValue((prev) => ({
                                        ...prev,
                                        [key]: e.target.value,
                                      }))
                                    }
                                  />
                                </Col>
                              </React.Fragment>
                            )
                          )
                        : null}

                      {detail?.is_register && detail?.type === 'DEFAULT' ? (
                        <Col x={24}>
                          <Content maxWidth={'100%'} padding={0}>
                            <Button
                              type="primary"
                              size="large"
                              disabled={!selectedUsers.length}
                              onClick={() => onSubmit()}
                            >
                              알림톡 발송하기
                            </Button>
                          </Content>
                        </Col>
                      ) : null}
                    </Row>
                  </Content>
                </Content>
              ) : (
                <Content
                  maxWidth={'100%'}
                  borderRadius={4}
                  height={'100%'}
                  padding={0}
                ></Content>
              )}
            </Col>

            <Col x={14}>
              <Row gutter={[5, 5]}>
                <Col x={24}>
                  <Content maxWidth={'100%'} borderRadius={4} height={'100%'}>
                    {/* 검색 */}
                    <div style={{ ...styles.filterWrap }}>
                      {FilterItems.map((item, index) => (
                        <div
                          key={`${item.key}_${index}`}
                          style={{
                            ...styles.filterItem,
                            ...(index === 0
                              ? { borderTop: '1px solid #DDDDDD' }
                              : {}),
                          }}
                        >
                          <div style={{ ...styles.filterLabel }}>
                            <Text weight={700} maxLine={3} size={14}>
                              {item.title}
                            </Text>
                          </div>
                          <div style={{ ...styles.filterValue }}>
                            <Checkbox.Group
                              options={item.children}
                              value={filter[item.key]}
                              onChange={(e) => handleSelected(item.key, e)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <Content
                        maxWidth={'100%'}
                        padding={'24px 0px'}
                        style={{
                          display: 'flex',
                          gap: 12,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          size="large"
                          onClick={() => handleSearchList('reset')}
                        >
                          초기화
                        </Button>
                        <Button
                          size="large"
                          type="primary"
                          onClick={() => handleSearchList('search')}
                        >
                          검색
                        </Button>
                      </Content>
                    </div>
                  </Content>
                </Col>

                <Col x={24}>
                  <Content maxWidth={'100%'} borderRadius={4} height={'100%'}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <Text size={14} color={'#666666'}>
                        검색된 회원{' '}
                        <span style={{ fontWeight: 700, color: '#111111' }}>
                          {stringToMoneyFormat(userList.length, '명')}
                        </span>
                      </Text>

                      <Text size={14} color={'#666666'}>
                        선택된 회원{' '}
                        <span style={{ fontWeight: 700, color: '#111111' }}>
                          {stringToMoneyFormat(selectedUsers.length, '명')}
                        </span>
                      </Text>

                      <Button onClick={() => onUserSelect('ALL', userList)}>
                        검색된 회원 전체{' '}
                        {userList.length === selectedUsers.length
                          ? '해제'
                          : '선택'}
                      </Button>
                    </div>

                    <Table
                      columns={columns}
                      dataSource={userList}
                      rowSelection={{
                        type: 'checkbox',
                        onSelect: (record, selected) => {
                          onUserSelect(
                            selected ? 'checked' : 'unchecked',
                            record
                          );
                        },
                        onSelectAll: (selected, selectedRows) => {
                          onUserSelect(
                            selected ? 'all-checked' : 'all-unchecked',
                            selectedRows
                          );
                        },
                        selectedRowKeys: selectedUsers?.map(
                          (user) => user?.key
                        ),
                      }}
                    />
                  </Content>
                </Col>
              </Row>
            </Col>
          </Row>
        </Content>
      </Spin>
    </React.Fragment>
  );
};

export default AlimTalkPresenter;
